import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { calendarAPI, notificationAPI } from '../services/api';
import { 
  Settings as SettingsIcon, 
  Calendar, 
  Bell, 
  Check, 
  X, 
  Loader2, 
  Link2, 
  Link2Off,
  Download,
  RefreshCw,
  Mail,
  Smartphone,
  Sun,
  Moon,
  Palette,
  Zap,
  Flower2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const Settings = () => {
  const { user } = useAuth();
  const { theme, setTheme } = useTheme();
  const [calendarConnections, setCalendarConnections] = useState(null);
  const [notificationPrefs, setNotificationPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({});
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);

  useEffect(() => {
    loadSettings();
    checkPushSupport();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const [calRes, notifRes] = await Promise.all([
        calendarAPI.getConnections(),
        notificationAPI.getPreferences()
      ]);
      setCalendarConnections(calRes.data);
      setNotificationPrefs(notifRes.data);
    } catch (error) {
      console.error('Failed to load settings:', error);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const checkPushSupport = () => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    
    if (supported && 'Notification' in window) {
      setPushSubscribed(Notification.permission === 'granted');
    }
  };

  const handleConnectGoogle = async () => {
    try {
      const response = await calendarAPI.getGoogleAuthUrl();
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Google Calendar integration not configured');
    }
  };

  const handleDisconnectGoogle = async () => {
    try {
      await calendarAPI.disconnectGoogle();
      toast.success('Google Calendar disconnected');
      loadSettings();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleConnectMicrosoft = async () => {
    try {
      const response = await calendarAPI.getMicrosoftAuthUrl();
      window.location.href = response.data.authorization_url;
    } catch (error) {
      toast.error('Microsoft Calendar integration not configured');
    }
  };

  const handleDisconnectMicrosoft = async () => {
    try {
      await calendarAPI.disconnectMicrosoft();
      toast.success('Microsoft Calendar disconnected');
      loadSettings();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleExportApple = async () => {
    try {
      const response = await calendarAPI.exportAppleCalendar(30);
      const blob = new Blob([response.data], { type: 'text/calendar' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'hearmeout_calendar.ics';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Calendar exported! Import into Apple Calendar.');
    } catch (error) {
      toast.error('Failed to export calendar');
    }
  };

  const handleSyncCalendar = async (provider) => {
    setSyncing({ ...syncing, [provider]: true });
    try {
      const response = await calendarAPI.syncCalendar(provider, true, true);
      toast.success(`Synced ${response.data.synced_count} events to ${provider}`);
      loadSettings();
    } catch (error) {
      toast.error(`Failed to sync to ${provider}`);
    } finally {
      setSyncing({ ...syncing, [provider]: false });
    }
  };

  const handleSubscribePush = async () => {
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error('Notification permission denied');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const vapidRes = await notificationAPI.getVapidPublicKey();
      
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidRes.data.publicKey)
      });

      await notificationAPI.subscribe({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
        }
      });

      setPushSubscribed(true);
      toast.success('Push notifications enabled!');
    } catch (error) {
      console.error('Push subscription failed:', error);
      toast.error('Failed to enable push notifications');
    }
  };

  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
    const rawData = window.atob(base64);
    return Uint8Array.from([...rawData].map(char => char.charCodeAt(0)));
  };

  const handleUpdatePreferences = async (key, value) => {
    const newPrefs = { ...notificationPrefs, [key]: value };
    setNotificationPrefs(newPrefs);
    
    try {
      await notificationAPI.updatePreferences(newPrefs);
      toast.success('Preferences updated');
    } catch (error) {
      toast.error('Failed to update preferences');
      setNotificationPrefs(notificationPrefs);
    }
  };

  const handleTestNotification = async () => {
    try {
      await notificationAPI.sendTestNotification('Test Notification', 'This is a test from HearMeOut!');
      toast.success('Test notification sent!');
    } catch (error) {
      toast.error('Failed to send test notification');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" style={{ color: 'var(--sunrise-orange)' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <SettingsIcon className="w-8 h-8" style={{ color: 'var(--sunrise-orange)' }} />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'Nunito, sans-serif', color: 'var(--text-black)' }}>
            Settings
          </h1>
        </div>

        {/* Appearance / Theme Section */}
        <div className="feature-card mb-6" data-testid="theme-settings">
          <div className="flex items-center gap-3 mb-6">
            <Palette className="w-6 h-6" style={{ color: 'var(--sunrise-orange)' }} />
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-black)' }}>Appearance</h2>
          </div>
          
          <p className="mb-6" style={{ color: 'var(--text-medium)' }}>
            Choose your preferred theme for the application. Preview each theme below.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {/* Sunrise Theme Preview */}
            <button
              onClick={() => setTheme('light')}
              className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                theme === 'light' ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                borderColor: theme === 'light' ? '#FFA500' : 'var(--card-border)',
                ringColor: theme === 'light' ? '#FFA500' : 'transparent'
              }}
              data-testid="theme-light-option"
            >
              {/* Theme Preview Swatch */}
              <div 
                className="h-28 relative"
                style={{ background: 'linear-gradient(135deg, #FFF6E0 0%, #FFDA63 100%)' }}
              >
                {/* Mini UI Preview */}
                <div className="absolute inset-2 flex flex-col gap-1.5 p-2">
                  {/* Mini Navbar */}
                  <div className="h-3 rounded-full" style={{ background: 'rgba(255, 165, 0, 0.3)', width: '100%' }}></div>
                  {/* Mini Cards */}
                  <div className="flex gap-1 flex-1">
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.9)' }}></div>
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.9)' }}></div>
                  </div>
                  {/* Mini Button */}
                  <div className="h-2.5 rounded-full mx-auto" style={{ background: '#FFA500', width: '60%' }}></div>
                </div>
                {/* Selected Badge */}
                {theme === 'light' && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#FFA500' }}>
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              {/* Theme Info */}
              <div className="p-3" style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Sun className="w-4 h-4" style={{ color: '#FFA500' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-black)' }}>Sunrise</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-light)' }}>Warm & Energizing</p>
                {/* Color Palette */}
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFF6E0' }} title="Background"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFA500' }} title="Primary"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFDA63' }} title="Accent"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#333333' }} title="Text"></div>
                </div>
              </div>
            </button>

            {/* Dark Theme Preview */}
            <button
              onClick={() => setTheme('dark')}
              className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                theme === 'dark' ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                borderColor: theme === 'dark' ? '#FFA500' : 'var(--card-border)',
                ringColor: theme === 'dark' ? '#FFA500' : 'transparent'
              }}
              data-testid="theme-dark-option"
            >
              {/* Theme Preview Swatch */}
              <div 
                className="h-28 relative"
                style={{ background: 'linear-gradient(135deg, #1A1F2E 0%, #2A3441 100%)' }}
              >
                {/* Mini UI Preview */}
                <div className="absolute inset-2 flex flex-col gap-1.5 p-2">
                  {/* Mini Navbar */}
                  <div className="h-3 rounded-full" style={{ background: 'rgba(255, 165, 0, 0.4)', width: '100%' }}></div>
                  {/* Mini Cards */}
                  <div className="flex gap-1 flex-1">
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(42, 52, 65, 0.9)' }}></div>
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(42, 52, 65, 0.9)' }}></div>
                  </div>
                  {/* Mini Button */}
                  <div className="h-2.5 rounded-full mx-auto" style={{ background: '#FFA500', width: '60%' }}></div>
                </div>
                {/* Selected Badge */}
                {theme === 'dark' && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#FFA500' }}>
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              {/* Theme Info */}
              <div className="p-3" style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Moon className="w-4 h-4" style={{ color: '#FFA500' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-black)' }}>Dark</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-light)' }}>Easy on Eyes</p>
                {/* Color Palette */}
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: '#1A1F2E' }} title="Background"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFA500' }} title="Primary"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#2A3441' }} title="Card"></div>
                  <div className="w-4 h-4 rounded-full border border-gray-500" style={{ background: '#E5E5E5' }} title="Text"></div>
                </div>
              </div>
            </button>

            {/* Cyberpunk Theme Preview */}
            <button
              onClick={() => setTheme('cyberpunk')}
              className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                theme === 'cyberpunk' ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                borderColor: theme === 'cyberpunk' ? '#00FFFF' : 'var(--card-border)',
                ringColor: theme === 'cyberpunk' ? '#FF00FF' : 'transparent',
                boxShadow: theme === 'cyberpunk' ? '0 0 20px rgba(255, 0, 255, 0.3)' : 'none'
              }}
              data-testid="theme-cyberpunk-option"
            >
              {/* Theme Preview Swatch */}
              <div 
                className="h-28 relative"
                style={{ background: 'linear-gradient(135deg, #0D0221 0%, #1A0A2E 100%)' }}
              >
                {/* Neon Grid Lines */}
                <div className="absolute inset-0 opacity-30" style={{ 
                  backgroundImage: 'linear-gradient(#FF00FF 1px, transparent 1px), linear-gradient(90deg, #00FFFF 1px, transparent 1px)',
                  backgroundSize: '20px 20px'
                }}></div>
                {/* Mini UI Preview */}
                <div className="absolute inset-2 flex flex-col gap-1.5 p-2">
                  {/* Mini Navbar */}
                  <div className="h-3 rounded-full" style={{ background: 'linear-gradient(90deg, #FF00FF, #00FFFF)', width: '100%' }}></div>
                  {/* Mini Cards */}
                  <div className="flex gap-1 flex-1">
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 0, 255, 0.2)', border: '1px solid #FF00FF' }}></div>
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(0, 255, 255, 0.2)', border: '1px solid #00FFFF' }}></div>
                  </div>
                  {/* Mini Button */}
                  <div className="h-2.5 rounded-full mx-auto" style={{ background: 'linear-gradient(90deg, #FF00FF, #00FFFF)', width: '60%' }}></div>
                </div>
                {/* Selected Badge */}
                {theme === 'cyberpunk' && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #FF00FF, #00FFFF)' }}>
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              {/* Theme Info */}
              <div className="p-3" style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Zap className="w-4 h-4" style={{ color: '#00FFFF' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-black)' }}>Cyberpunk</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-light)' }}>Neon Future</p>
                {/* Color Palette */}
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: '#0D0221' }} title="Background"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FF00FF' }} title="Magenta"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#00FFFF' }} title="Cyan"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFE600' }} title="Yellow"></div>
                </div>
              </div>
            </button>

            {/* Floral Theme Preview */}
            <button
              onClick={() => setTheme('floral')}
              className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                theme === 'floral' ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                borderColor: theme === 'floral' ? '#D4AF37' : 'var(--card-border)',
                ringColor: theme === 'floral' ? '#D4AF37' : 'transparent'
              }}
              data-testid="theme-floral-option"
            >
              {/* Theme Preview Swatch */}
              <div 
                className="h-28 relative"
                style={{ 
                  background: 'linear-gradient(135deg, #FFFDF5 0%, #FFF8E7 100%)',
                  backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'60\' height=\'60\' viewBox=\'0 0 60 60\'%3E%3Cg fill=\'%23D4AF37\' fill-opacity=\'0.15\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'4\'/%3E%3Ccircle cx=\'30\' cy=\'20\' r=\'3\'/%3E%3Ccircle cx=\'30\' cy=\'40\' r=\'3\'/%3E%3Ccircle cx=\'20\' cy=\'30\' r=\'3\'/%3E%3Ccircle cx=\'40\' cy=\'30\' r=\'3\'/%3E%3C/g%3E%3C/svg%3E")'
                }}
              >
                {/* Mini UI Preview */}
                <div className="absolute inset-2 flex flex-col gap-1.5 p-2">
                  {/* Mini Navbar */}
                  <div className="h-3 rounded-full" style={{ background: 'rgba(212, 175, 55, 0.4)', width: '100%' }}></div>
                  {/* Mini Cards */}
                  <div className="flex gap-1 flex-1">
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 254, 245, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)' }}></div>
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 254, 245, 0.95)', border: '1px solid rgba(212, 175, 55, 0.3)' }}></div>
                  </div>
                  {/* Mini Button */}
                  <div className="h-2.5 rounded-full mx-auto" style={{ background: 'linear-gradient(135deg, #D4AF37, #B8860B)', width: '60%' }}></div>
                </div>
                {/* Selected Badge */}
                {theme === 'floral' && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#D4AF37' }}>
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              {/* Theme Info */}
              <div className="p-3" style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Flower2 className="w-4 h-4" style={{ color: '#D4AF37' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-black)' }}>Floral</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-light)' }}>Elegant & Golden</p>
                {/* Color Palette */}
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full border border-gray-200" style={{ background: '#FFFDF5' }} title="Background"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#D4AF37' }} title="Gold"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#B8860B' }} title="Dark Gold"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#5D4E37' }} title="Text"></div>
                </div>
              </div>
            </button>

            {/* Anime Theme Preview */}
            <button
              onClick={() => setTheme('anime')}
              className={`group relative overflow-hidden rounded-2xl border-2 transition-all duration-300 hover:scale-[1.02] ${
                theme === 'anime' ? 'ring-2 ring-offset-2' : ''
              }`}
              style={{
                borderColor: theme === 'anime' ? '#E07030' : 'var(--card-border)',
                ringColor: theme === 'anime' ? '#E07030' : 'transparent'
              }}
              data-testid="theme-anime-option"
            >
              {/* Theme Preview Swatch */}
              <div 
                className="h-28 relative"
                style={{ 
                  background: 'linear-gradient(135deg, #FFE4C4 0%, #E07030 50%, #C04030 100%)'
                }}
              >
                {/* Anime-style mountains silhouette */}
                <div className="absolute bottom-0 left-0 right-0 h-10" style={{
                  background: 'linear-gradient(180deg, transparent 0%, rgba(45, 24, 16, 0.3) 100%)'
                }}></div>
                {/* Mini UI Preview */}
                <div className="absolute inset-2 flex flex-col gap-1.5 p-2">
                  {/* Mini Navbar */}
                  <div className="h-3 rounded-full" style={{ background: 'rgba(224, 112, 48, 0.5)', width: '100%' }}></div>
                  {/* Mini Cards */}
                  <div className="flex gap-1 flex-1">
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 252, 248, 0.92)', backdropFilter: 'blur(4px)' }}></div>
                    <div className="flex-1 rounded-lg" style={{ background: 'rgba(255, 252, 248, 0.92)', backdropFilter: 'blur(4px)' }}></div>
                  </div>
                  {/* Mini Button */}
                  <div className="h-2.5 rounded-full mx-auto" style={{ background: 'linear-gradient(135deg, #E07030, #C04030)', width: '60%' }}></div>
                </div>
                {/* Selected Badge */}
                {theme === 'anime' && (
                  <div className="absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center" style={{ background: '#E07030' }}>
                    <Check className="w-4 h-4 text-white" />
                  </div>
                )}
              </div>
              {/* Theme Info */}
              <div className="p-3" style={{ background: 'var(--card-bg)' }}>
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-4 h-4" style={{ color: '#E07030' }} />
                  <span className="font-semibold text-sm" style={{ color: 'var(--text-black)' }}>Anime</span>
                </div>
                <p className="text-xs" style={{ color: 'var(--text-light)' }}>Autumn Scenery</p>
                {/* Color Palette */}
                <div className="flex gap-1 mt-2">
                  <div className="w-4 h-4 rounded-full" style={{ background: '#FFE4C4' }} title="Sky"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#E07030' }} title="Orange"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#C04030' }} title="Red"></div>
                  <div className="w-4 h-4 rounded-full" style={{ background: '#2D1810' }} title="Text"></div>
                </div>
              </div>
            </button>
          </div>

          <p className="text-xs mt-4" style={{ color: 'var(--text-light)' }}>
            Your theme preference is saved automatically and will persist across sessions.
          </p>
        </div>

        {/* Calendar Integrations */}
        <div className="feature-card mb-6" data-testid="calendar-settings">
          <div className="flex items-center gap-3 mb-6">
            <Calendar className="w-6 h-6" style={{ color: '#3B82F6' }} />
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-black)' }}>Calendar Integrations</h2>
          </div>
          
          <p className="mb-6" style={{ color: 'var(--text-medium)' }}>
            Connect your calendars to sync medication reminders and doctor appointments.
          </p>

          <div className="space-y-4">
            {/* Google Calendar */}
            <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Google Calendar</p>
                  <p className="text-sm text-muted-foreground">
                    {calendarConnections?.google?.connected 
                      ? `Connected: ${calendarConnections.google.email}`
                      : 'Not connected'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {calendarConnections?.google?.connected ? (
                  <>
                    <button
                      onClick={() => handleSyncCalendar('google')}
                      disabled={syncing.google}
                      className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                      title="Sync Now"
                    >
                      {syncing.google ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={handleDisconnectGoogle}
                      className="p-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                      title="Disconnect"
                    >
                      <Link2Off className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleConnectGoogle}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    data-testid="connect-google-calendar"
                  >
                    <Link2 className="w-4 h-4" />
                    Connect
                  </button>
                )}
              </div>
            </div>

            {/* Microsoft Outlook */}
            <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-[#0078D4] rounded-lg flex items-center justify-center">
                  <svg viewBox="0 0 24 24" className="w-6 h-6 text-white" fill="currentColor">
                    <path d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86t.1-.87q.1-.43.34-.76.22-.34.59-.54.36-.2.87-.2t.86.2q.35.21.57.55.22.34.31.77.1.43.1.88zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V12zm-6-8.25v3h3v-3h-3zm0 4.5v3h3v-3h-3zm0 4.5v1.83l3.05-1.83H18zm-5.25-9v3h3.75v-3h-3.75zm0 4.5v3h3.75v-3h-3.75zm0 4.5v2.03l2.41 1.5 1.34-.8v-2.73h-3.75zM9 3.75V6h2l.13.01.12.04v-2.3H9zM6.88 15.04q0-.45.11-.86.1-.42.34-.75.22-.33.57-.54.36-.2.88-.2.53 0 .88.2t.55.54q.22.33.32.75.1.41.1.86 0 .45-.11.87-.1.41-.33.74-.21.33-.57.52-.35.2-.87.2-.53 0-.88-.2-.34-.21-.55-.55-.22-.34-.32-.77-.1-.42-.1-.86z"/>
                  </svg>
                </div>
                <div>
                  <p className="font-semibold text-foreground">Microsoft Outlook</p>
                  <p className="text-sm text-muted-foreground">
                    {calendarConnections?.microsoft?.connected 
                      ? `Connected: ${calendarConnections.microsoft.email}`
                      : 'Not connected'}
                  </p>
                </div>
              </div>
              <div className="flex gap-2">
                {calendarConnections?.microsoft?.connected ? (
                  <>
                    <button
                      onClick={() => handleSyncCalendar('microsoft')}
                      disabled={syncing.microsoft}
                      className="p-2 bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                      title="Sync Now"
                    >
                      {syncing.microsoft ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />}
                    </button>
                    <button
                      onClick={handleDisconnectMicrosoft}
                      className="p-2 bg-red-500/10 text-red-500 rounded-lg hover:bg-red-500/20 transition-colors"
                      title="Disconnect"
                    >
                      <Link2Off className="w-5 h-5" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={handleConnectMicrosoft}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                    data-testid="connect-microsoft-calendar"
                  >
                    <Link2 className="w-4 h-4" />
                    Connect
                  </button>
                )}
              </div>
            </div>

            {/* Apple Calendar */}
            <div className="flex items-center justify-between p-4 bg-background rounded-xl border border-border">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-red-500 to-orange-500 rounded-lg flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-foreground">Apple Calendar</p>
                  <p className="text-sm text-muted-foreground">
                    Export .ics file to import into Apple Calendar
                  </p>
                </div>
              </div>
              <button
                onClick={handleExportApple}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
                data-testid="export-apple-calendar"
              >
                <Download className="w-4 h-4" />
                Export
              </button>
            </div>
          </div>
        </div>

        {/* Notification Preferences */}
        <div className="bg-card rounded-2xl p-6 border border-border" data-testid="notification-settings">
          <div className="flex items-center gap-3 mb-6">
            <Bell className="w-6 h-6 text-yellow-400" />
            <h2 className="text-xl font-bold text-foreground">Notification Preferences</h2>
          </div>

          <p className="text-muted-foreground mb-6">
            Control how you receive reminders and alerts.
          </p>

          <div className="space-y-4">
            {/* Notification Channels */}
            <div className="p-4 bg-background rounded-xl border border-border">
              <h3 className="font-semibold text-foreground mb-4">Notification Channels</h3>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Smartphone className="w-5 h-5 text-blue-400" />
                    <span className="text-foreground">Browser Push Notifications</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pushSupported ? (
                      pushSubscribed ? (
                        <span className="text-xs text-green-500 flex items-center gap-1">
                          <Check className="w-4 h-4" /> Enabled
                        </span>
                      ) : (
                        <button
                          onClick={handleSubscribePush}
                          className="text-xs px-3 py-1 bg-primary text-primary-foreground rounded-full"
                        >
                          Enable
                        </button>
                      )
                    ) : (
                      <span className="text-xs text-muted-foreground">Not supported</span>
                    )}
                    <input
                      type="checkbox"
                      checked={notificationPrefs?.browser_push ?? true}
                      onChange={(e) => handleUpdatePreferences('browser_push', e.target.checked)}
                      className="w-5 h-5 rounded"
                    />
                  </div>
                </label>

                <label className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Mail className="w-5 h-5 text-green-400" />
                    <span className="text-foreground">Email Notifications</span>
                  </div>
                  <input
                    type="checkbox"
                    checked={notificationPrefs?.email_notifications ?? true}
                    onChange={(e) => handleUpdatePreferences('email_notifications', e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                </label>
              </div>
            </div>

            {/* Notification Types */}
            <div className="p-4 bg-background rounded-xl border border-border">
              <h3 className="font-semibold text-foreground mb-4">Notification Types</h3>
              
              <div className="space-y-3">
                <label className="flex items-center justify-between">
                  <span className="text-foreground">💊 Medication Reminders</span>
                  <input
                    type="checkbox"
                    checked={notificationPrefs?.medication_reminders ?? true}
                    onChange={(e) => handleUpdatePreferences('medication_reminders', e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span className="text-foreground">🩺 Appointment Reminders</span>
                  <input
                    type="checkbox"
                    checked={notificationPrefs?.appointment_reminders ?? true}
                    onChange={(e) => handleUpdatePreferences('appointment_reminders', e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                </label>

                <label className="flex items-center justify-between">
                  <span className="text-foreground">🧠 MindPulse Wellness Alerts</span>
                  <input
                    type="checkbox"
                    checked={notificationPrefs?.mindpulse_alerts ?? true}
                    onChange={(e) => handleUpdatePreferences('mindpulse_alerts', e.target.checked)}
                    className="w-5 h-5 rounded"
                  />
                </label>
              </div>
            </div>

            {/* Reminder Timing */}
            <div className="p-4 bg-background rounded-xl border border-border">
              <h3 className="font-semibold text-foreground mb-4">Reminder Timing</h3>
              
              <label className="flex items-center justify-between">
                <span className="text-foreground">Remind me before events</span>
                <select
                  value={notificationPrefs?.reminder_time_before_minutes ?? 30}
                  onChange={(e) => handleUpdatePreferences('reminder_time_before_minutes', parseInt(e.target.value))}
                  className="px-3 py-2 bg-input border border-border rounded-lg text-foreground"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={120}>2 hours</option>
                </select>
              </label>
            </div>

            {/* Test Notification */}
            <button
              onClick={handleTestNotification}
              className="w-full py-3 bg-primary/10 text-primary rounded-xl font-semibold hover:bg-primary/20 transition-colors"
            >
              Send Test Notification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
